import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IState, State } from 'country-state-city';
import { Role } from '../../../core/enum/role.enum';
import { CountryCode } from '../../../core/models/country/country-code';
import { UserProfile } from '../../../core/models/user/user-profile';
import { CountriesService } from '../../../core/services/countries.service';
import { DefaultCountryCode } from '../../../core/services/constant';
import { FormValidators } from '../../../core/validator/form.validators';

// INC-289 Phase 1: contact fields (address, postcode, city, countryId,
// stateId, phoneCountryCode, phoneNumber) extracted verbatim from
// profile-details.component.ts's buildForm() / getStates() / changeCountry()
// so the standalone settings page and the Phase-2 review-attest edit sheet
// share ONE copy of the validators and error messages. `email` is contact
// data but READONLY on the page (external change-email link) — it stays on
// the page, not in this component.
@Component({
  standalone: false,
  selector: 'app-contact-section-form',
  templateUrl: './contact-section-form.component.html',
  styleUrls: ['./contact-section-form.component.scss'],
})
export class ContactSectionFormComponent implements OnInit, OnChanges {
  @Input() profile: UserProfile;
  @Input() role: Role;
  @Input() showFormError = false;

  form: UntypedFormGroup;
  countries: CountryCode[];
  prioritisedCountriesCount: number;
  states: IState[];
  Role: typeof Role = Role;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private countriesService: CountriesService,
  ) {}

  ngOnInit(): void {
    const prioritisedCountries = ['MY', 'SG', 'US'];
    this.countries = this.countriesService.getSortedCountryList(prioritisedCountries);
    this.prioritisedCountriesCount = prioritisedCountries.length;
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Guarded on `this.form` since the very first change (the one Angular
    // fires before ngOnInit) arrives before buildForm() has run; ngOnInit
    // does that initial seed instead.
    if (changes.profile && this.profile && this.form) {
      this.seedFromProfile();
    }
  }

  buildForm(): void {
    this.form = this.formBuilder.group(
      {
        address: ['', this.role != Role.CompanyRep ? Validators.required : null],
        postcode: ['', this.role != Role.CompanyRep ? Validators.required : null],
        city: ['', this.role != Role.CompanyRep ? Validators.required : null],
        countryId: [null, this.role != Role.CompanyRep ? Validators.required : null],
        stateId: [''],
        phoneCountryCode: [
          DefaultCountryCode,
          this.role != Role.CompanyRep ? Validators.required : null,
        ],
        phoneNumber: ['', this.role != Role.CompanyRep ? Validators.required : null],
      },
      {
        validators: [FormValidators.countryCode('phoneNumber', 'phoneCountryCode')],
      },
    );

    if (this.profile) {
      this.seedFromProfile();
    }
  }

  private seedFromProfile(): void {
    this.form.patchValue(this.profile);

    const countryId = this.form.value.countryId;
    if (countryId) {
      this.getStates(countryId);
    }
  }

  changeCountry(id: string): void {
    this.form.controls.stateId.setValue(null);
    this.getStates(id);
  }

  getStates(id: string): void {
    this.states = State.getStatesOfCountry(id);

    if (this.states && this.states.length > 0)
      this.form.controls.stateId.setValidators([Validators.required]);
    else this.form.controls.stateId.clearValidators();

    this.form.controls.stateId.updateValueAndValidity();
  }
}
